import { getAssetStreamUrl } from '@/lib/api/media-proxy';
import { getAssetBackedVideoPosterUrl, isDurableAssetId, shouldUseDurableMediaUrl } from '@/lib/expiring-media-url';
import { getTaskResultImageUrl } from '@/lib/image-utils';
import {
  toAbsoluteMediaUrl,
  toDisplayMediaUrl,
  unwrapNextImageOptimizationUrl,
} from '@/lib/media-url';

const VIDEO_FILE_RE = /\.(mp4|webm|mov|m3u8)(\?|$)/i;
const SNAPSHOT_MARKER = 'ci-process=snapshot';
const IMAGE_FILE_RE = /\.(avif|gif|jpe?g|png|webp|svg)(\?|#|$)/i;

function shouldPreferAssetStream(videoUrl?: string | null, assetId?: string | null): boolean {
  if (!assetId) return false;
  if (!videoUrl) return true;
  if (videoUrl.startsWith('blob:') || videoUrl.startsWith('data:')) return false;
  if (videoUrl.startsWith('/api/assets/stream/')) return false;
  return false;
}

function uniqueMediaUrls(urls: Array<string | undefined | null>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const url of urls) {
    if (!url) continue;
    const normalized = toAbsoluteMediaUrl(toDisplayMediaUrl(url.trim()));
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
  }
  return result;
}

export function buildVideoPlaybackUrls(options: {
  videoUrl?: string | null;
  assetId?: string | null;
}): string[] {
  const directUrl = options.videoUrl || '';
  const assetStreamUrl = isDurableAssetId(options.assetId) ? getAssetStreamUrl(options.assetId) : '';
  return shouldPreferAssetStream(directUrl, options.assetId) || (assetStreamUrl && shouldUseDurableMediaUrl(directUrl))
    ? uniqueMediaUrls([assetStreamUrl, ...(shouldUseDurableMediaUrl(directUrl) ? [] : [directUrl])])
    : uniqueMediaUrls([directUrl, assetStreamUrl]);
}

export function isVideoFileUrl(url?: string | null): boolean {
  return !!url && !isDerivedVideoSnapshotUrl(url) && VIDEO_FILE_RE.test(url);
}

export function isAssetStreamUrl(url?: string | null): boolean {
  if (!url) return false;
  const normalized = toDisplayMediaUrl(url.trim());
  return normalized.startsWith('/api/assets/stream/');
}

export function isImageLikeThumbnailUrl(url?: string | null): boolean {
  if (!url) return false;
  const normalized = url.trim();
  if (!normalized) return false;
  if (isDerivedVideoSnapshotUrl(normalized)) return true;
  if (isVideoFileUrl(normalized)) return false;
  if (IMAGE_FILE_RE.test(normalized)) return true;

  try {
    const parsed = new URL(normalized, 'https://dobidobi.local');
    const pathname = parsed.pathname.toLowerCase();
    return IMAGE_FILE_RE.test(pathname);
  } catch {
    return false;
  }
}

export function isPersistableThumbnailUrl(url?: string | null): boolean {
  if (!url) return false;
  const normalized = url.trim();
  if (!normalized) return false;
  if (normalized.startsWith('blob:') || normalized.startsWith('data:')) return false;
  if (normalized.startsWith('pending/') || normalized.includes('/pending/')) return false;
  return isImageLikeThumbnailUrl(normalized);
}

export function getPersistableThumbnailUrl(
  thumbnailUrl?: string | null,
  mediaType?: 'image' | 'video' | '3d_model' | 'audio' | 'prompt' | 'sequence' | null,
): string | undefined {
  if (!thumbnailUrl) return undefined;
  const normalized = toDisplayMediaUrl(unwrapNextImageOptimizationUrl(thumbnailUrl));
  if (!normalized) return undefined;
  if (isVideoFileUrl(normalized)) return undefined;
  if (mediaType === 'video' || mediaType === '3d_model') {
    return isPersistableThumbnailUrl(normalized) ? normalized : undefined;
  }
  if (normalized.startsWith('blob:') || normalized.startsWith('data:')) return undefined;
  if (normalized.startsWith('pending/') || normalized.includes('/pending/')) return undefined;
  return normalized;
}

export function isDerivedVideoSnapshotUrl(url?: string | null): boolean {
  if (!url) return false;

  try {
    return decodeURIComponent(url).includes(SNAPSHOT_MARKER);
  } catch {
    return url.includes(SNAPSHOT_MARKER);
  }
}

export function buildVideoSnapshotUrl(videoUrl?: string | null, width = 720): string | undefined {
  void width;
  void videoUrl;
  return undefined;
}

export function getDisplayThumbnailUrl(
  thumbnailUrl?: string | null,
  mediaType?: 'image' | 'video' | null,
): string | undefined {
  if (!thumbnailUrl) return undefined;
  if (isVideoFileUrl(thumbnailUrl)) return undefined;
  if (mediaType === 'video' && !isImageLikeThumbnailUrl(thumbnailUrl)) {
    return undefined;
  }
  return toDisplayMediaUrl(thumbnailUrl.trim());
}

export function resolveDisplayVideoSource(options: {
  videoUrl?: string | null;
  thumbnailUrl?: string | null;
  assetId?: string | null;
}): { playbackUrl: string; playbackUrls: string[]; fallbackUrls: string[]; posterUrl?: string } {
  const safeThumbnailUrl = getAssetBackedVideoPosterUrl(
    getDisplayThumbnailUrl(options.thumbnailUrl, 'video'), options.assetId,
  );
  const playbackUrls = buildVideoPlaybackUrls({
    videoUrl: options.videoUrl,
    assetId: options.assetId,
  });
  const playbackUrl = playbackUrls[0] || '';
  const posterUrl = safeThumbnailUrl
    ? toAbsoluteMediaUrl(toDisplayMediaUrl(safeThumbnailUrl))
    : undefined;

  return {
    playbackUrl,
    playbackUrls,
    fallbackUrls: playbackUrls.slice(1),
    ...(posterUrl ? { posterUrl } : {}),
  };
}

export function resolveDisplayTaskMedia(options: {
  resultType?: 'image' | 'video' | null;
  previewUrl?: string | null;
  outputUrl?: string | null;
  thumbnailUrl?: string | null;
  assetId?: string | null;
}): {
  isVideo: boolean;
  mediaUrl: string;
  previewUrl: string;
  posterUrl?: string;
  mediaUrls?: string[];
  fallbackMediaUrls?: string[];
  hasDisplayMedia: boolean;
} {
  const sourceUrl = options.outputUrl || options.previewUrl || '';
  const isVideo = options.resultType === 'video' || isVideoFileUrl(sourceUrl);

  if (isVideo) {
    const previewIsVideo = isVideoFileUrl(options.previewUrl);
    const videoUrl = options.outputUrl || (previewIsVideo ? options.previewUrl : '') || '';
    const thumbnailUrl = options.thumbnailUrl || (!videoUrl && options.previewUrl ? options.previewUrl : undefined);
    const { playbackUrl, playbackUrls, fallbackUrls, posterUrl } = resolveDisplayVideoSource({
      videoUrl,
      thumbnailUrl,
      assetId: options.assetId,
    });
    const previewUrl = posterUrl || '';

    return {
      isVideo: true,
      mediaUrl: playbackUrl,
      previewUrl,
      ...(posterUrl ? { posterUrl } : {}),
      mediaUrls: playbackUrls,
      fallbackMediaUrls: fallbackUrls,
      hasDisplayMedia: !!(playbackUrl || posterUrl),
    };
  }

  const mediaUrl = getTaskResultImageUrl(sourceUrl, options.assetId);
  const previewThumbnailUrl = getDisplayThumbnailUrl(options.thumbnailUrl, 'image');
  const previewUrl = previewThumbnailUrl
    ? toAbsoluteMediaUrl(toDisplayMediaUrl(previewThumbnailUrl))
    : mediaUrl;

  return {
    isVideo: false,
    mediaUrl,
    previewUrl,
    hasDisplayMedia: !!(mediaUrl || previewUrl),
  };
}
